import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Progress } from '../ui/progress';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import FeelingsNeedsHelper from '../FeelingsNeedsHelper';
import EbookPageFooter from '../shared/EbookPageFooter';
import FloatingFeelingsHelper from '../shared/FloatingFeelingsHelper';
import {
  FeelIcon, NeedIcon, GoldIcon, ObserveIcon,
  ExerciseIcon, HomeworkIcon, PartnerIcon,
  VideoIcon, ReadingIcon, SaveIcon, ChapterIcon,
  ArrowRightIcon, ArrowLeftIcon, CheckIcon, InsightIcon,
  GiftIcon, RequestIcon, SparkleIcon, FearIcon, IdeasIcon,
  ExpandIcon, CollapseIcon, ResetIcon, DownloadIcon,
  CloudIcon, CloudOffIcon, ViewIcon, HideIcon
} from '../icons/ElchmeIcons';

const CHAPTER_IMAGES = {
  hero: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=1200&q=80',
  requests: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=800&q=80',
  connection: 'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=800&q=80',
  growth: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80',
};

// Exercise 1 - 4 requests with feeling if "No"
const exercise1Prompts = [
  { id: 1, request: '', feelingIfNo: '' },
  { id: 2, request: '', feelingIfNo: '' },
  { id: 3, request: '', feelingIfNo: '' },
  { id: 4, request: '', feelingIfNo: '' },
];

// Exercise 2 - Positive Action Language Requests (p115)
const exercise2Data = [
  {
    id: 1,
    context: "I noticed that you have asked me three times to change the oil in the car. I'm irritated because I have a need for independence.",
    prompt: "Would you be willing to"
  },
  {
    id: 2,
    context: "When I hear you speak to me that way, I feel afraid because I have a need for safety in our relationship.",
    prompt: "Would you be willing to"
  },
  {
    id: 3,
    context: "I am realizing that I haven't called you in a few weeks. I feel disappointed because I value our relationship.",
    prompt: "Would you be willing to"
  },
  {
    id: 4,
    context: "I see that you are coughing and blowing your nose and I feel concerned because I have a need for your well-being.",
    prompt: "Would you"
  },
  {
    id: 5,
    context: "When I think about our current financial obligations, I feel overwhelmed because I have a need for ease and support.",
    prompt: "Would you be willing to"
  },
  {
    id: 6,
    context: "When you told me that you're not as attracted to me anymore, I felt hurt because I have a need for acceptance and appreciation of who I am.",
    prompt: "Would you be willing to"
  },
];

// Exercise 3 - Translation exercises (p116)
const exercise3Data = [
  { id: 1, statement: '"Don\'t you know how expensive that is? We can\'t afford it."' },
  { id: 2, statement: '"Would you stop telling me what to do?"' },
  { id: 3, statement: '"Don\'t waste your time talking to him."' },
  { id: 4, statement: '"I don\'t feel like talking right now. Just leave me alone."' },
  { id: 5, statement: '"When are you going to grow up?"' },
  { id: 6, statement: '"This is clearly the best decision. You should do it."' },
  { id: 7, statement: '"I just can\'t deal with you anymore."' },
];

// Exercise 3 Answers (p133)
const exercise3Answers = [
  { id: 1, whenI: "hear your desire to purchase that item", iFeel: "afraid", becauseINeed: "financial security", areYouWilling: "tell me what needs of yours you are trying to meet by purchasing this item?" },
  { id: 2, whenI: "hear you speak to me in that way", iFeel: "irritated", becauseINeed: "freedom", areYouWilling: "tell me what you heard me say?" },
  { id: 3, whenI: "hear you say that", iFeel: "upset", becauseINeed: "compassion for others", areYouWilling: "tell me what is going on for you having heard me say that?" },
  { id: 4, whenI: "see your attempt to talk with me", iFeel: "guarded", becauseINeed: "space", areYouWilling: "talk with me in 3 hours?" },
  { id: 5, whenI: "see you playing video games 4 hours per day this week", iFeel: "agitated", becauseINeed: "cooperation", areYouWilling: "talk with me about how we can both get our needs met?" },
  { id: 6, whenI: "hear you say this", iFeel: "confused", becauseINeed: "autonomy", areYouWilling: "hear what I think about this decision?" },
  { id: 7, whenI: "hear you tell me that I'm wrong", iFeel: "frustrated", becauseINeed: "respect", areYouWilling: "discuss how we can talk with each other that works for both of us?" },
];

// Exercise 4 - Needs-based requests (p117)
const exercise4Needs = [
  { id: 'respect', name: 'Respect', requests: ['', ''] },
  { id: 'selfExpression', name: 'Self-Expression', requests: ['', ''] },
  { id: 'meaning', name: 'Meaning or Purpose in Life', requests: ['', ''] },
  { id: 'freedom', name: 'Freedom', requests: ['', ''] },
];

// Common Fears and Beliefs (p118)
const commonFears = [
  { id: 1, text: "I fear the pain of hearing rejection." },
  { id: 2, text: "I fear the pain of not being received or understood." },
  { id: 3, text: "I fear the pain of not being able to express my request clearly." },
  { id: 4, text: "I fear the pain of being triggered by past experiences." },
  { id: 5, text: "I fear the pain of being hurt or angry if the other person says \"No\" to me." },
  { id: '6a', text: "I fear being viewed as \"needy\" \"weak\" \"pitiful\"" },
  { id: '6b', text: "I fear being viewed as \"manipulative\" \"sleazy\"" },
  { id: '6c', text: "I fear being viewed as \"greedy\" \"grabby\"" },
  { id: '6d', text: "I fear being viewed as \"ignorant\" \"naïve\" \"uncool\"" },
  { id: '6e', text: "I fear being viewed as \"demanding\" \"aggressive\"" },
  { id: 7, text: "I fear the pain of viewing myself in ways I disapprove." },
  { id: 8, text: "I believe that I do not deserve what I am requesting." },
  { id: 9, text: "I believe that a request is pointless since it will definitely be rejected." },
  { id: 10, text: "I believe that the other party lacks the capacity to satisfy my request." },
  { id: 11, text: "I believe that I should be self-reliant and able to cope without asking for help." },
  { id: 12, text: "I believe that it is disrespectful or inconsiderate to put people in the potentially uncomfortable position of \"having to say 'No'.\"" },
  { id: 13, text: "I believe that the other person should know what I want without my having to ask for it." },
];

// 101 Ideas for Offering and Asking (p126-129)
const ideasList = [
  "Read a book to me", "Read a poem to me", "Go for a walk with me", "Go for a drive on the parkway with me",
  "Take me to one of your favorite nature spots", "Take me to a place in nature you've never been before but are curious",
  "Take me to a restaurant and buy me my favorite dessert", "Stay home and make me my favorite dessert",
  "Come over to my house and let me make you my favorite dessert", "Invite me over to watch one of your favorite movies",
  "Come over to my house and watch one of my favorite movies", "Tell me about some of your favorite memories growing up",
  "Take me to an ethnic restaurant that you really like", "Take me to an ethnic restaurant you've never been to but are curious about",
  "Play basketball with me", "Play frisbee with me", "Play tennis with me", "Throw the football with me",
  "Invite me over to listen to your favorite music", "Invite me over to dance to your favorite music",
  "Come over to my house and listen to my favorite music with me", "Come over to my house and dance to my favorite music with me",
  "Show me pictures of your favorite people and tell me stories of them", "Let me show you pictures of my favorite people and tell stories about them",
  "Tell me some of your favorite jokes", "Ask me to tell you some of my favorite jokes",
  "Tell me stories about the first people in your family to come to the US", "Read me a poem or a song that you have written",
  "Ask me to read you a poem or a song that I have written", "Tell me about the countries that you have visited",
  "Tell me your favorite work-related stories", "Tell me about the greatest heroes in your life",
  "Help me clean my house", "Help me clean my yard", "Help me organize my house", "Go work out with me",
  "Watch one of my favorite old time TV shows together", "Go mushroom hunting with me",
  "Go wild foods foraging with me", "Drum together", "Drum and dance together", "Listen to classical music together",
  "Show me your astrological chart and tell me whatever you know about it", "Ask me to show you my astrological chart and share about it",
  "Go on a bike ride together", "Go to an amusement park together", "Go swimming in a river or lake",
  "Visit a waterfall", "Walk through downtown giving money away with me", "Walk through downtown asking for money with me",
  "Help me create a vision board of my ideal life", "Ask me to help you create a vision board of your ideal life",
  "Play music together on instruments", "Exchange back massages and/or foot rubs",
  "Tell favorite stories of your parents", "Tell favorite stories of your children",
  "Make art in nature using only natural materials", "Make a video together and post it online",
  "Dress up as historical figures and go to dinner together", "Write a love letter to a mutual friend and send it to them",
  "Make a get well card and send it to a mutual friend", "Paint a piece of art together, frame it, and give it to Goodwill",
  "Paint a piece of art together, frame it, and give it to a friend", "Come over and play with my children so I can have some alone time",
  "Come and play with my children so my partner and I can have a date night", "Drive me blindfolded and see if I can guess where we are",
  "Go to a theatre to see a movie with me", "Come to my house to do a fix it project",
  "Invite me to your house to do a fix it project", "Attend an event at a church, synagogue, meditation center, etc",
  "Walk around your neighborhood doing random acts of kindness", "Visit a local waterfall",
  "Go to a spa to do a sauna and/or hot tub", "Play with a tarot deck or other divination cards",
  "Go to a yoga class together", "Play with enjoying the smell of essential oils",
  "Share about the most enjoyable moments of your life", "Share about the greatest achievements of your life",
  "Share about the most memorable moments of your love life", "Offer to bring someone breakfast in bed",
  "Ask someone to bring you breakfast in bed", "Go for a walk on the beach",
  "Go for a walk or ride in the car go where your intuition takes you", "Go to a zoo or a nature preserve",
  "Go to a bookstore or library exploring each other's interests", "Watch a sunrise together",
  "Watch the sunset together", "Go to an animal shelter and spend time caring for them",
  "Play your favorite board game or card game", "Learn and/or share breathing practices",
  "Watch your favorite childhood cartoons", "Read or watch something from one of your favorite authors",
  "Spend time together in the woods or park hugging trees", "Go to a playground together and watch children play",
  "Go to a playground together and play like you were five years old again", "Go to a u-pick farm together",
  "Walk or run around in a rain storm together", "Share what you are most grateful for about each other",
  "Share what you are most grateful for about your life", "Push each other on a swing",
  "Lay on the ground at night and look up at the stars"
];

// 101 Magic Words and Phrases sample (p130-131)
const magicPhrases = [
  "Thank you for being in my life", "I am really glad you are in my life", "I really appreciate having you in my life",
  "What a gift you are to me", "I am so grateful for the ways you enrich my life", "Thank you for all the ways you make my life more wonderful",
  "I love going through life with you", "I am so grateful to have you as my friend", "I am so grateful for all of the good times we have shared together",
  "I am so grateful for all the ways that we make each other's lives more wonderful", "I love being in your life", "I am so glad I'm in your life",
  "I am so glad to know you", "I love all the ways I get to make your life more wonderful", "I am so happy to be on this journey together",
  "I love doing life with you", "What a blessing to be sharing life with you", "I love going through life with you",
  "Knowing you is such a joy", "Lucky me that I get to be your friend", "I really want you to thrive",
  "I really want you to get your needs met", "I really want to help you get your needs met", "I really want to know the ways that I can contribute to your life",
  "It feels really good to me to contribute to your well-being", "I am so glad I get to help you out",
  "Thank you for asking for what you need", "Thank you for asking for what's important to you", "Thank you for telling me what you need",
  "Thank you for telling me what's important to you", "I feel so safe with you", "I love how safe I feel with you",
  "I am so grateful that I feel so safe with you", "I really want you to feel safe with me", "I love how safe you feel with me",
  "Please tell me what I can say or do to help you feel more safe with me", "Thank you for telling me what you need to feel safe with me",
  "I love that we feel so safe with each other", "I love that we can tell each other what we need to feel safe",
  "I love how committed we are to feeling safe with each other"
];

export default function Chapter5Presentation() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialSection = parseInt(searchParams.get('section')) || 0;
  const [currentSection, setCurrentSection] = useState(initialSection);
  const [isPresentationMode, setIsPresentationMode] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [isHelperOpen, setIsHelperOpen] = useState(false);
  const [expandedItem, setExpandedItem] = useState(null);
  const [showAnswers, setShowAnswers] = useState({});

  // Exercise 1 responses
  const [exercise1Responses, setExercise1Responses] = useState(() => {
    const saved = localStorage.getItem('chapter5Exercise1');
    return saved ? JSON.parse(saved) : {};
  });

  // Exercise 2 responses
  const [exercise2Responses, setExercise2Responses] = useState(() => {
    const saved = localStorage.getItem('chapter5Exercise2');
    return saved ? JSON.parse(saved) : {};
  });

  // Exercise 3 responses
  const [exercise3Responses, setExercise3Responses] = useState(() => {
    const saved = localStorage.getItem('chapter5Exercise3');
    return saved ? JSON.parse(saved) : {};
  });

  // Exercise 4 responses
  const [exercise4Responses, setExercise4Responses] = useState(() => {
    const saved = localStorage.getItem('chapter5Exercise4');
    return saved ? JSON.parse(saved) : {};
  });

  // What Keeps Me responses
  const [whatKeepsMeResponses, setWhatKeepsMeResponses] = useState(() => {
    const saved = localStorage.getItem('chapter5WhatKeepsMe');
    return saved ? JSON.parse(saved) : { situation: '', observation: '', want: '', feelings: '', fears: {}, otherFear: '' };
  });

  // Page 125 worksheet
  const [page125Responses, setPage125Responses] = useState(() => {
    const saved = localStorage.getItem('chapter5Page125');
    return saved ? JSON.parse(saved) : { requests: [], offers: [] };
  });

  // Sections with PDF page references
  const sections = [
    { id: 'intro', title: 'Chapter 5: Requests', page: 114 },
    { id: 'exercise1', title: 'Exercise 1: Request or Demand?', page: 114 },
    { id: 'exercise2', title: 'Exercise 2: Positive Action Language', page: 115 },
    { id: 'exercise3', title: 'Exercise 3: Translate to OFNR', page: 116 },
    { id: 'exercise3-answers', title: 'Exercise 3: Answers', page: 133 },
    { id: 'exercise4', title: 'Exercise 4: Needs-Based Requests', page: 117 },
    { id: 'what-keeps-me', title: 'What Keeps Me from Requesting?', page: 118 },
    { id: 'fears-beliefs', title: 'Common Fears & Beliefs', page: 118 },
    { id: 'reading-dont-criticize', title: "Don't Criticize Me vs Do-able Requests", page: 121 },
    { id: 'reading-every-request', title: 'When Every Request is a Demand', page: 123 },
    { id: 'page125', title: "Let's Make Our Life More Wonderful", page: 125 },
    { id: '101-ideas', title: '101 Ideas for Offering & Asking', page: 126 },
    { id: '101-magic', title: '101 Magic Words & Phrases', page: 130 },
    { id: 'homework', title: 'Chapter 5 Homework', page: 134 },
  ];

  const progress_pct = ((currentSection + 1) / sections.length) * 100;

  // Check login status
  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsLoggedIn(!!token);
  }, []);

  // Save progress
  const saveProgress = async () => {
    localStorage.setItem('chapter5Exercise1', JSON.stringify(exercise1Responses));
    localStorage.setItem('chapter5Exercise2', JSON.stringify(exercise2Responses));
    localStorage.setItem('chapter5Exercise3', JSON.stringify(exercise3Responses));
    localStorage.setItem('chapter5Exercise4', JSON.stringify(exercise4Responses));
    localStorage.setItem('chapter5WhatKeepsMe', JSON.stringify(whatKeepsMeResponses));
    localStorage.setItem('chapter5Page125', JSON.stringify(page125Responses));
    
    if (isLoggedIn) {
      setIsSyncing(true);
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/workbook-progress/chapter5`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ 
            exercise1Responses,
            exercise2Responses,
            exercise3Responses,
            exercise4Responses,
            whatKeepsMeResponses,
            page125Responses
          })
        });
        if (response.ok) {
          setLastSyncTime(new Date());
          toast.success('Progress saved to your account!');
        }
      } catch (error) {
        toast.error('Saved locally (server sync failed)');
      } finally {
        setIsSyncing(false);
      }
    } else {
      toast.success('Saved locally! Sign in to sync across devices.');
    }
  };

  const resetAll = () => {
    if (window.confirm('Reset all Chapter 5 responses? This cannot be undone.')) {
      setExercise1Responses({});
      setExercise2Responses({});
      setExercise3Responses({});
      setExercise4Responses({});
      setWhatKeepsMeResponses({ situation: '', observation: '', want: '', feelings: '', fears: {}, otherFear: '' });
      setPage125Responses({ requests: [], offers: [] });
      localStorage.removeItem('chapter5Exercise1');
      localStorage.removeItem('chapter5Exercise2');
      localStorage.removeItem('chapter5Exercise3');
      localStorage.removeItem('chapter5Exercise4');
      localStorage.removeItem('chapter5WhatKeepsMe');
      localStorage.removeItem('chapter5Page125');
      toast.success('All responses reset!');
    }
  };

  // Download as PDF
  const downloadPDF = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please allow popups to download the PDF');
      return;
    }
    
    const currentDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

    const htmlContent = `
<!DOCTYPE html>
<html><head><meta charset="UTF-8"><title>Chapter 5: Requests</title>
<style>
  body { font-family: Georgia, serif; line-height: 1.6; color: #1a1a1a; padding: 40px; max-width: 800px; margin: 0 auto; }
  .header { text-align: center; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 3px solid #7C3AED; }
  .header h1 { font-size: 28px; color: #7C3AED; margin-bottom: 8px; }
  .section { margin: 25px 0; page-break-inside: avoid; }
  .section h2 { font-size: 18px; color: #7C3AED; border-bottom: 2px solid #7C3AED; padding-bottom: 8px; margin-bottom: 15px; }
  .exercise-item { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 8px; padding: 15px; margin: 10px 0; }
  .label { font-weight: bold; color: #555; font-size: 13px; margin-bottom: 5px; }
  .value { background: white; border: 1px solid #ddd; border-radius: 4px; padding: 10px; margin-bottom: 10px; min-height: 25px; }
  .value.empty { color: #999; font-style: italic; }
  .footer { margin-top: 40px; text-align: center; font-size: 12px; color: #888; border-top: 1px solid #ddd; padding-top: 20px; }
</style></head><body>
  <div class="header">
    <h1>Chapter 5: Requests</h1>
    <div>Pages 114-134 • Belonging Sessions E-book</div>
    <div style="margin-top: 10px; color: #666;">${currentDate}</div>
  </div>
  
  <div class="section">
    <h2>Exercise 1: Request or Demand?</h2>
    ${[1,2,3,4].map(i => `
      <div class="exercise-item">
        <div class="label">Request ${i}:</div>
        <div class="value ${!exercise1Responses[`req${i}`] ? 'empty' : ''}">${exercise1Responses[`req${i}`] || '(Not completed)'}</div>
        <div class="label">How I would feel if met with "No":</div>
        <div class="value ${!exercise1Responses[`feel${i}`] ? 'empty' : ''}">${exercise1Responses[`feel${i}`] || '(Not completed)'}</div>
      </div>
    `).join('')}
  </div>
  
  <div class="section">
    <h2>Exercise 2: Positive Action Language</h2>
    ${exercise2Data.map((item, i) => `
      <div class="exercise-item">
        <div class="label">${i+1}. "${item.context}"</div>
        <div class="label">${item.prompt}...</div>
        <div class="value ${!exercise2Responses[item.id] ? 'empty' : ''}">${exercise2Responses[item.id] || '(Not completed)'}</div>
      </div>
    `).join('')}
  </div>
  
  <div class="footer">
    <p>Generated from <strong>Belonging Sessions</strong> E-book</p>
    <p>"Requests are a gift — they let others know how to contribute to your life."</p>
  </div>
</body></html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.onload = () => { setTimeout(() => printWindow.print(), 250); };
    toast.success('PDF download started!');
  };

  // Toggle presentation mode
  const togglePresentationMode = async () => {
    if (!isPresentationMode) {
      try {
        if (document.documentElement.requestFullscreen) {
          await document.documentElement.requestFullscreen();
        }
      } catch (e) { console.log('Fullscreen not available'); }
    } else {
      try {
        if (document.fullscreenElement) {
          await document.exitFullscreen();
        }
      } catch (e) { console.log('Exit fullscreen failed'); }
    }
    setIsPresentationMode(!isPresentationMode);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isPresentationMode) {
        if (e.key === 'ArrowRight' || e.key === ' ') {
          e.preventDefault();
          setCurrentSection(prev => Math.min(prev + 1, sections.length - 1));
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          setCurrentSection(prev => Math.max(prev - 1, 0));
        } else if (e.key === 'Escape') {
          setIsPresentationMode(false);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isPresentationMode, sections.length]);

  // Pulsing Feelings & Needs CTA
  const FeelingsNeedsCTA = ({ className = '' }) => (
    <Button
      onClick={() => setIsHelperOpen(true)}
      className={`relative bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-500 hover:to-pink-500 text-white ${className}`}
      data-testid="feelings-needs-cta"
    >
      <div className="absolute inset-0 rounded-md bg-rose-500/30 animate-ping" style={{ animationDuration: '2s' }} />
      <span className="relative flex items-center gap-2">
        <FeelIcon size={18} />
        Feelings & Needs Tool
        <NeedIcon size={18} />
      </span>
    </Button>
  );

  // Render content based on section
  const renderContent = () => {
    switch (currentSection) {
      // INTRO - Page 114
      case 0:
        return (
          <div className="space-y-8 animate-fadeIn" data-testid="chapter5-intro">
            <div className="relative rounded-xl overflow-hidden shadow-2xl h-64 lg:h-96">
              <img src={CHAPTER_IMAGES.hero} alt="Requests" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 to-transparent flex items-end p-8">
                <div>
                  <h2 className="text-4xl lg:text-5xl font-bold text-white mb-2">Chapter 5: Requests</h2>
                  <p className="text-xl text-purple-200">Pages 114-134 • The Art of Asking</p>
                </div>
              </div>
            </div>
            
            <div className="flex justify-center">
              <FeelingsNeedsCTA className="px-6 py-3 text-lg" />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-purple-900/20 border border-purple-700/30 rounded-xl p-6">
                <RequestIcon size={32} className="mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">Clear Requests</h3>
                <p className="text-slate-300">
                  Learn to make requests that are <span className="text-purple-300 font-semibold">positive, concrete, and do-able</span> — 
                  not demands disguised as questions.
                </p>
              </div>
              <div className="bg-amber-900/20 border border-amber-700/30 rounded-xl p-6">
                <GiftIcon size={32} className="mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">Requests as Gifts</h3>
                <p className="text-slate-300">
                  Discover how requests can be <span className="text-amber-300 font-semibold">gifts</span> that 
                  invite others to contribute to your life.
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-900/40 to-pink-900/30 border-2 border-purple-500/50 rounded-xl p-6">
              <p className="text-xl text-purple-100 italic text-center">
                "A request is something the other person can say 'no' to without fear of punishment, withdrawal of affection, guilt tripping, or judgment."
              </p>
            </div>

            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6 text-center">
              <p className="text-slate-300 text-lg">
                In this chapter, you'll practice making <span className="text-purple-400">do-able requests</span>, 
                explore what keeps you from asking, and discover <span className="text-amber-400">101 ways</span> to make life more wonderful together.
              </p>
            </div>
          </div>
        );

      // EXERCISE 1 - Page 114
      case 1:
        return (
          <div className="space-y-6 animate-fadeIn" data-testid="exercise1">
            <div className="text-center mb-6">
              <ExerciseIcon size={40} className="mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-white mb-2">Exercise 1: Request or Demand?</h2>
              <p className="text-purple-300">Page 114 • Check if your requests are truly requests</p>
            </div>

            <div className="flex justify-center mb-6">
              <FeelingsNeedsCTA />
            </div>

            <div className="bg-purple-900/30 border border-purple-500/30 rounded-xl p-6 mb-6">
              <p className="text-purple-100 leading-relaxed">
                <strong>Instructions:</strong> Write down four requests you have made in the last week. 
                Next, check inside and be honest about how you might feel if the request and the need that it represents were not met.
                <span className="block mt-2 text-purple-200 italic">
                  This is a way to check if your request is a demand or a request.
                </span>
              </p>
            </div>

            <div className="space-y-6">
              {[1, 2, 3, 4].map((num) => (
                <div key={num} className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold">{num}</span>
                    </div>
                    <h3 className="text-lg font-semibold text-white">Request {num}</h3>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-purple-300 mb-2 block">My Request:</label>
                      <Textarea
                        value={exercise1Responses[`req${num}`] || ''}
                        onChange={(e) => setExercise1Responses(prev => ({ ...prev, [`req${num}`]: e.target.value }))}
                        placeholder="What request did you make?"
                        className="bg-slate-900/50 border-slate-600 text-white min-h-[60px]"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-rose-300 mb-2 block">
                        How I would honestly feel if my request is met with a "No":
                      </label>
                      <Textarea
                        value={exercise1Responses[`feel${num}`] || ''}
                        onChange={(e) => setExercise1Responses(prev => ({ ...prev, [`feel${num}`]: e.target.value }))}
                        placeholder="Be honest with yourself..."
                        className="bg-slate-900/50 border-slate-600 text-white min-h-[60px]"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-amber-900/30 border border-amber-500/30 rounded-xl p-5">
              <div className="flex items-start gap-3">
                <InsightIcon size={24} color="#F59E0B" className="flex-shrink-0 mt-1" />
                <p className="text-amber-100">
                  <strong>Reflection:</strong> If you notice you would feel angry, resentful, or want to punish 
                  the other person for saying "No," your request may actually be a demand. 
                  A true request allows the other person freedom to decline without consequences.
                </p>
              </div>
            </div>
          </div>
        );

      // EXERCISE 2 - Page 115
      case 2:
        return (
          <div className="space-y-6 animate-fadeIn" data-testid="exercise2">
            <div className="text-center mb-6">
              <ExerciseIcon size={40} className="mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-white mb-2">Exercise 2: Positive Action Language</h2>
              <p className="text-purple-300">Page 115 • Complete with do-able requests</p>
            </div>

            <div className="flex justify-center mb-6">
              <FeelingsNeedsCTA />
            </div>

            <div className="bg-purple-900/30 border border-purple-500/30 rounded-xl p-5 mb-6">
              <p className="text-purple-100">
                Complete the following scenarios with <strong className="text-purple-200">positive action language requests</strong> — 
                requests that are specific, do-able, and state what you <em>do</em> want (not what you <em>don't</em> want).
              </p>
            </div>

            <div className="space-y-6">
              {exercise2Data.map((item, idx) => (
                <div key={item.id} className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-sm">{idx + 1}</span>
                    </div>
                  </div>
                  
                  <p className="text-slate-200 mb-4 italic">"{item.context}"</p>
                  
                  <div className="flex items-center gap-2">
                    <span className="text-purple-300 font-medium whitespace-nowrap">{item.prompt}</span>
                    <Input
                      value={exercise2Responses[item.id] || ''}
                      onChange={(e) => setExercise2Responses(prev => ({ ...prev, [item.id]: e.target.value }))}
                      placeholder="..."
                      className="bg-slate-900/50 border-slate-600 text-white flex-1"
                    />
                    <span className="text-purple-300">?</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      // EXERCISE 3 - Page 116
      case 3:
        return (
          <div className="space-y-6 animate-fadeIn" data-testid="exercise3">
            <div className="text-center mb-6">
              <ExerciseIcon size={40} className="mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-white mb-2">Exercise 3: Translate to OFNR</h2>
              <p className="text-purple-300">Page 116 • Translate statements to feelings & needs</p>
            </div>

            <div className="flex justify-center mb-6">
              <FeelingsNeedsCTA />
            </div>

            <div className="bg-purple-900/30 border border-purple-500/30 rounded-xl p-5 mb-6">
              <p className="text-purple-100 mb-4">
                Imagine situations where someone makes the following statements. In each case, translate the statement 
                using all four components of "formal NVC," paying special attention that the request is <strong>positive, concrete, and immediately do-able</strong>.
              </p>
              <div className="bg-slate-800/50 rounded-lg p-4">
                <p className="text-sm text-slate-300 mb-2"><strong>Example:</strong> "You did such a terrible job setting the table. Let me do it."</p>
                <p className="text-emerald-300 text-sm">When I see you setting the table in that way</p>
                <p className="text-rose-300 text-sm">I feel nervous because I need beauty and order</p>
                <p className="text-purple-300 text-sm">Are you willing to talk with me to find an approach that will work for both of us?</p>
              </div>
            </div>

            <div className="space-y-6">
              {exercise3Data.map((item, idx) => (
                <div key={item.id} className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center">
                        <span className="text-white font-bold text-sm">{idx + 1}</span>
                      </div>
                      <p className="text-slate-200 italic">{item.statement}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-3 ml-11">
                    <div className="flex items-center gap-2">
                      <span className="text-emerald-400 font-medium w-20">When I</span>
                      <Input
                        value={exercise3Responses[`${item.id}-when`] || ''}
                        onChange={(e) => setExercise3Responses(prev => ({ ...prev, [`${item.id}-when`]: e.target.value }))}
                        placeholder="observe/hear..."
                        className="bg-slate-900/50 border-slate-600 text-white flex-1"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-rose-400 font-medium w-20">I feel</span>
                      <Input
                        value={exercise3Responses[`${item.id}-feel`] || ''}
                        onChange={(e) => setExercise3Responses(prev => ({ ...prev, [`${item.id}-feel`]: e.target.value }))}
                        placeholder="feeling..."
                        className="bg-slate-900/50 border-slate-600 text-white flex-1"
                      />
                      <span className="text-rose-400 font-medium whitespace-nowrap">because I need</span>
                      <Input
                        value={exercise3Responses[`${item.id}-need`] || ''}
                        onChange={(e) => setExercise3Responses(prev => ({ ...prev, [`${item.id}-need`]: e.target.value }))}
                        placeholder="need..."
                        className="bg-slate-900/50 border-slate-600 text-white flex-1"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-purple-400 font-medium whitespace-nowrap">Are you willing to</span>
                      <Input
                        value={exercise3Responses[`${item.id}-request`] || ''}
                        onChange={(e) => setExercise3Responses(prev => ({ ...prev, [`${item.id}-request`]: e.target.value }))}
                        placeholder="do-able request..."
                        className="bg-slate-900/50 border-slate-600 text-white flex-1"
                      />
                      <span className="text-purple-400">?</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      // EXERCISE 3 ANSWERS - Page 133
      case 4:
        return (
          <div className="space-y-6 animate-fadeIn" data-testid="exercise3-answers">
            <div className="text-center mb-6">
              <CheckIcon size={40} className="mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-white mb-2">Exercise 3: Possible Answers</h2>
              <p className="text-purple-300">Page 133 • Compare your responses</p>
            </div>

            <div className="flex justify-center mb-6">
              <FeelingsNeedsCTA />
            </div>

            <div className="bg-amber-900/30 border border-amber-500/30 rounded-xl p-5 mb-6">
              <div className="flex items-start gap-3">
                <InsightIcon size={24} color="#F59E0B" className="flex-shrink-0 mt-1" />
                <p className="text-amber-100">
                  These are <strong>possible</strong> answers. Your responses may be different and equally valid! 
                  The goal is to practice the NVC structure, not to get the "right" answer.
                </p>
              </div>
            </div>

            <div className="space-y-6">
              {exercise3Answers.map((answer, idx) => (
                <div key={answer.id} className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center">
                        <span className="text-white font-bold text-sm">{idx + 1}</span>
                      </div>
                      <p className="text-slate-400 italic text-sm">{exercise3Data[idx].statement}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowAnswers(prev => ({ ...prev, [answer.id]: !prev[answer.id] }))}
                      className="text-purple-400 hover:text-purple-300"
                    >
                      {showAnswers[answer.id] ? <HideIcon size={16} className="mr-1" /> : <ViewIcon size={16} className="mr-1" />}
                      {showAnswers[answer.id] ? 'Hide' : 'Reveal'}
                    </Button>
                  </div>
                  
                  {showAnswers[answer.id] && (
                    <div className="space-y-2 ml-11 animate-in fade-in">
                      <p className="text-emerald-300">
                        <span className="text-emerald-400 font-medium">When I</span> {answer.whenI}
                      </p>
                      <p className="text-rose-300">
                        <span className="text-rose-400 font-medium">I feel</span> {answer.iFeel} <span className="text-rose-400 font-medium">because I need</span> {answer.becauseINeed}
                      </p>
                      <p className="text-purple-300">
                        <span className="text-purple-400 font-medium">Are you willing to</span> {answer.areYouWilling}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="flex justify-center mt-6">
              <Button
                onClick={() => setShowAnswers(prev => {
                  const allShown = exercise3Answers.every(a => prev[a.id]);
                  const newState = {};
                  exercise3Answers.forEach(a => { newState[a.id] = !allShown; });
                  return newState;
                })}
                className="bg-purple-600 hover:bg-purple-500"
              >
                {exercise3Answers.every(a => showAnswers[a.id]) ? 'Hide All Answers' : 'Reveal All Answers'}
              </Button>
            </div>
          </div>
        );

      // EXERCISE 4 - Page 117
      case 5:
        return (
          <div className="space-y-6 animate-fadeIn" data-testid="exercise4">
            <div className="text-center mb-6">
              <ExerciseIcon size={40} className="mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-white mb-2">Exercise 4: Needs-Based Requests</h2>
              <p className="text-purple-300">Page 117 • Create specific actions from needs</p>
            </div>

            <div className="flex justify-center mb-6">
              <FeelingsNeedsCTA />
            </div>

            <div className="bg-purple-900/30 border border-purple-500/30 rounded-xl p-5 mb-6">
              <p className="text-purple-100 mb-4">
                For each of the following needs, write down <strong>two things another person could say or do</strong> that would 
                help meet that need for you. This exercise is designed to help you create specific actions to get your needs met.
              </p>
              <div className="bg-slate-800/50 rounded-lg p-4">
                <p className="text-sm text-slate-300 mb-2"><strong>Example: Friendship</strong></p>
                <p className="text-emerald-300 text-sm">1. Would you like to go on a hike this Saturday with me?</p>
                <p className="text-emerald-300 text-sm">2. Our friendship is important to me and I would like to talk with you about something that I am concerned about. Would you be willing and do you have time to talk with me now?</p>
              </div>
            </div>

            <div className="space-y-6">
              {exercise4Needs.map((need) => (
                <div key={need.id} className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
                  <h3 className="text-xl font-bold text-emerald-300 mb-4">{need.name}</h3>
                  
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <span className="text-emerald-400 font-bold">1.</span>
                      <Textarea
                        value={exercise4Responses[`${need.id}-1`] || ''}
                        onChange={(e) => setExercise4Responses(prev => ({ ...prev, [`${need.id}-1`]: e.target.value }))}
                        placeholder="Write a specific, do-able request..."
                        className="bg-slate-900/50 border-slate-600 text-white min-h-[60px] flex-1"
                      />
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-emerald-400 font-bold">2.</span>
                      <Textarea
                        value={exercise4Responses[`${need.id}-2`] || ''}
                        onChange={(e) => setExercise4Responses(prev => ({ ...prev, [`${need.id}-2`]: e.target.value }))}
                        placeholder="Write another specific, do-able request..."
                        className="bg-slate-900/50 border-slate-600 text-white min-h-[60px] flex-1"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      // WHAT KEEPS ME FROM MAKING A REQUEST - Page 118
      case 6:
        return (
          <div className="space-y-6 animate-fadeIn" data-testid="what-keeps-me">
            <div className="text-center mb-6">
              <InsightIcon size={40} color="#A855F7" className="mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-white mb-2">What Keeps Me from Making a Request?</h2>
              <p className="text-purple-300">Page 118 • A guided exploration</p>
            </div>

            <div className="flex justify-center mb-6">
              <FeelingsNeedsCTA />
            </div>

            <div className="bg-purple-900/30 border border-purple-500/30 rounded-xl p-6 mb-6">
              <p className="text-purple-100 italic">
                Use the following to guide your exploration and sharing...
              </p>
            </div>

            <div className="space-y-6">
              {/* Step 1-2 */}
              <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-sm">1</span>
                  </div>
                  <h3 className="text-lg font-semibold text-white">The Situation</h3>
                </div>
                <p className="text-slate-400 text-sm mb-3">
                  Bring to mind a situation where you would like (or wish you had been able) to make a request of someone. 
                  Describe the situation using NVC observations.
                </p>
                <Textarea
                  value={whatKeepsMeResponses.situation || ''}
                  onChange={(e) => setWhatKeepsMeResponses(prev => ({ ...prev, situation: e.target.value }))}
                  placeholder="Describe the situation objectively..."
                  className="bg-slate-900/50 border-slate-600 text-white min-h-[80px]"
                />
              </div>

              {/* Step 3 */}
              <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-sm">2</span>
                  </div>
                  <h3 className="text-lg font-semibold text-white">What You Want</h3>
                </div>
                <p className="text-slate-400 text-sm mb-3">
                  What would you want the other party to do or say?
                </p>
                <Textarea
                  value={whatKeepsMeResponses.want || ''}
                  onChange={(e) => setWhatKeepsMeResponses(prev => ({ ...prev, want: e.target.value }))}
                  placeholder="I would want them to..."
                  className="bg-slate-900/50 border-slate-600 text-white min-h-[60px]"
                />
              </div>

              {/* Step 4 */}
              <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 bg-rose-600 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-sm">3</span>
                  </div>
                  <h3 className="text-lg font-semibold text-white">Your Feelings</h3>
                </div>
                <p className="text-slate-400 text-sm mb-3">
                  What feelings come up when you think of making such a request to them?
                </p>
                <Textarea
                  value={whatKeepsMeResponses.feelings || ''}
                  onChange={(e) => setWhatKeepsMeResponses(prev => ({ ...prev, feelings: e.target.value }))}
                  placeholder="When I imagine making this request, I feel..."
                  className="bg-slate-900/50 border-slate-600 text-white min-h-[60px]"
                />
              </div>

              {/* Guidance for Steps 5-10 */}
              <div className="bg-gradient-to-br from-purple-900/40 to-pink-900/30 border border-purple-500/40 rounded-xl p-6">
                <h3 className="text-lg font-bold text-purple-300 mb-4">Continue Your Exploration</h3>
                <ol className="space-y-3 text-slate-200">
                  <li className="flex gap-3">
                    <span className="text-purple-400 font-bold">5.</span>
                    <span>What keeps you from making the request? (See the "Common Fears and Beliefs" on the next page)</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-400 font-bold">6.</span>
                    <span>For each fear/belief, ask: What is my need here? Am I meeting this need by NOT making the request?</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-400 font-bold">7.</span>
                    <span>When you choose not to make a request, what needs are left unmet?</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-400 font-bold">8.</span>
                    <span>Take a silent moment to hold and value both sets of needs. Notice any physical sensations.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-400 font-bold">9.</span>
                    <span>Practice making the request out loud, speaking as if addressing the other party.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-400 font-bold">10.</span>
                    <span>Share with your group how you feel after verbalizing the request.</span>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        );

      // COMMON FEARS AND BELIEFS - Page 118
      case 7:
        return (
          <div className="space-y-6 animate-fadeIn" data-testid="fears-beliefs">
            <div className="text-center mb-6">
              <FearIcon size={40} className="mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-white mb-2">Common Fears and Beliefs</h2>
              <p className="text-rose-300">Page 118 • Which Keep Me From Making Requests</p>
            </div>

            <div className="flex justify-center mb-6">
              <FeelingsNeedsCTA />
            </div>

            <div className="bg-rose-900/30 border border-rose-500/30 rounded-xl p-5 mb-6">
              <p className="text-rose-100">
                Check off those fears and beliefs that resonate with you. Then consider: What is my need here?
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {commonFears.map((fear) => (
                <div 
                  key={fear.id}
                  className={`bg-slate-800/50 border rounded-xl p-4 cursor-pointer transition-all ${
                    whatKeepsMeResponses.fears?.[fear.id] 
                      ? 'border-rose-500 bg-rose-900/20' 
                      : 'border-slate-700/50 hover:border-slate-600'
                  }`}
                  onClick={() => setWhatKeepsMeResponses(prev => ({
                    ...prev,
                    fears: { ...prev.fears, [fear.id]: !prev.fears?.[fear.id] }
                  }))}
                >
                  <div className="flex items-start gap-3">
                    <Checkbox 
                      checked={whatKeepsMeResponses.fears?.[fear.id] || false}
                      className="mt-1"
                    />
                    <span className="text-slate-200 text-sm">{fear.text}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Custom fear */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
              <label className="text-sm font-medium text-slate-300 mb-2 block">Add your own fear or belief:</label>
              <Input
                value={whatKeepsMeResponses.otherFear || ''}
                onChange={(e) => setWhatKeepsMeResponses(prev => ({ ...prev, otherFear: e.target.value }))}
                placeholder="I fear/believe that..."
                className="bg-slate-900/50 border-slate-600 text-white"
              />
            </div>
          </div>
        );

      // READING: DON'T CRITICIZE ME - Pages 121-123
      case 8:
        return (
          <div className="space-y-6 animate-fadeIn" data-testid="reading-dont-criticize">
            <div className="text-center mb-6">
              <ReadingIcon size={40} className="mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-white mb-2">"Don't Criticize Me!" vs. Do-able Requests</h2>
              <p className="text-amber-300">Pages 121-123 • LaShelle Charde's Connection Gem of the Week</p>
            </div>

            <div className="flex justify-center mb-6">
              <FeelingsNeedsCTA />
            </div>

            <div className="bg-gradient-to-br from-amber-900/30 to-orange-900/20 border border-amber-500/30 rounded-2xl p-8 space-y-6">
              <p className="text-slate-200 leading-relaxed">
                An effective request contains an action that is <strong className="text-amber-300">do-able</strong>. In the moment when something you don't like is coming your way, you want to remove it quickly. For example, your partner is telling you that he didn't like your behavior and you say, <em>"Stop criticizing me!"</em>
              </p>
              
              <p className="text-slate-200 leading-relaxed">
                Unfortunately this leaves the speaker with a lot of feelings and no way to express them. With no other suggestion, they are likely to continue to do what they know.
              </p>

              <div className="bg-slate-800/50 rounded-xl p-5 border-l-4 border-amber-500">
                <h4 className="text-amber-300 font-bold mb-3">Ann's Story</h4>
                <p className="text-slate-300 text-sm leading-relaxed">
                  Ann, a student in one of my classes, described a family dinner in which her mother said something like, <em>"People who voted for John Kerry are idiots."</em> Everyone at the table was aware that only Ann had voted for John Kerry. We worked on a role play for her to connect to her mom around this.
                </p>
              </div>

              <div className="bg-rose-900/30 rounded-xl p-5">
                <h4 className="text-rose-300 font-bold mb-3">❌ "Don't" Requests (NOT Do-able)</h4>
                <ul className="text-slate-300 text-sm space-y-1">
                  <li>• Don't criticize my views.</li>
                  <li>• Don't talk about politics at the dinner table.</li>
                  <li>• Don't judge me.</li>
                  <li>• Don't point me out in front of the family.</li>
                </ul>
              </div>

              <div className="bg-amber-900/30 rounded-xl p-5">
                <h4 className="text-amber-300 font-bold mb-3">⚠️ Vague Invitations (Still NOT Do-able)</h4>
                <ul className="text-slate-300 text-sm space-y-1">
                  <li>• Be more considerate.</li>
                  <li>• Think how I might feel when you say that.</li>
                  <li>• Respect my views.</li>
                </ul>
              </div>

              <div className="bg-emerald-900/30 rounded-xl p-5">
                <h4 className="text-emerald-300 font-bold mb-3">✅ Do-able Requests</h4>
                <p className="text-slate-200 text-sm leading-relaxed mb-3">
                  In the end, Ann came up with something like this:
                </p>
                <p className="text-emerald-200 italic">
                  "Mom, when you said, 'People who voted for John Kerry are idiots', at dinner last night I felt hurt and disappointed because I want connection. <strong>Would you be willing to say what issues you disagree with when we talk politics rather than labeling the voters?</strong>"
                </p>
              </div>

              <div className="bg-purple-900/30 rounded-xl p-5 mt-6">
                <h4 className="text-purple-300 font-bold mb-3">Guidelines for an Effective Request</h4>
                <ol className="text-slate-200 space-y-2">
                  <li><span className="text-purple-400 font-bold">1.</span> Requests are immediately preceded by a universal need</li>
                  <li><span className="text-purple-400 font-bold">2.</span> Requests are do-able: something a camera could record (visual or auditory). "Be more supportive," "Trust me" are NOT do-able.</li>
                  <li><span className="text-purple-400 font-bold">3.</span> Requests are something the other person can say "no" to without fear of punishment, withdrawal of affection, guilt tripping, or judgment. (Not a demand.)</li>
                </ol>
              </div>
            </div>
          </div>
        );

      // READING: WHEN EVERY REQUEST IS A DEMAND - Pages 123-124
      case 9:
        return (
          <div className="space-y-6 animate-fadeIn" data-testid="reading-every-request">
            <div className="text-center mb-6">
              <ReadingIcon size={40} color="#EC4899" className="mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-white mb-2">When Every Request is a Demand</h2>
              <p className="text-pink-300">Pages 123-124 • LaShelle Charde's Connection Gem of the Week</p>
            </div>

            <div className="flex justify-center mb-6">
              <FeelingsNeedsCTA />
            </div>

            <div className="bg-gradient-to-br from-pink-900/30 to-rose-900/20 border border-pink-500/30 rounded-2xl p-8 space-y-6">
              <p className="text-slate-200 leading-relaxed text-lg">
                If your partner has difficulty believing that s/he can have you and be authentic or autonomous, s/he may hear your <strong className="text-pink-300">every request as a threat</strong>.
              </p>

              <p className="text-slate-200 leading-relaxed">
                This limiting belief that intimacy and autonomy are mutually exclusive leaves him or her in a constant internal battle. This internal conflict may show up in indirect ways in your relationship.
              </p>

              <div className="bg-slate-800/50 rounded-xl p-5">
                <h4 className="text-rose-300 font-bold mb-3">Signs It Might Show Up</h4>
                <ul className="text-slate-300 space-y-2">
                  <li>• S/he says yes, but doesn't follow through on your requests.</li>
                  <li>• Reports feeling lost or disconnected from self.</li>
                  <li>• Expresses a desire to go out with you, but then takes an hour to find his keys.</li>
                  <li>• When asked her preference, answers with, "Well, what do you want to do?" or "I don't know, I'll see how I feel."</li>
                  <li>• Takes space indirectly by staying on the computer for hours.</li>
                  <li>• Makes sudden big decisions without talking with you.</li>
                </ul>
              </div>

              <div className="bg-emerald-900/30 rounded-xl p-5">
                <h4 className="text-emerald-300 font-bold mb-3">How to Help Heal This Pattern</h4>
                <ul className="text-slate-200 space-y-3">
                  <li className="flex gap-2">
                    <span className="text-emerald-400">•</span>
                    When your partner says yes to a request, ask them to take another minute and check-in and really make sure that works for them.
                  </li>
                  <li className="flex gap-2">
                    <span className="text-emerald-400">•</span>
                    When making a request, offer at least two options, e.g., "I would love your companionship for the banquet and if that doesn't work for you, I'm also happy to ask a friend to go."
                  </li>
                  <li className="flex gap-2">
                    <span className="text-emerald-400">•</span>
                    Offer reassurance that your love is not at risk when s/he expresses disagreement.
                  </li>
                  <li className="flex gap-2">
                    <span className="text-emerald-400">•</span>
                    Remind them frequently that you value their authenticity and choices.
                  </li>
                  <li className="flex gap-2">
                    <span className="text-emerald-400">•</span>
                    Before making a request, state that either a yes or no answer is truly okay with you.
                  </li>
                  <li className="flex gap-2">
                    <span className="text-emerald-400">•</span>
                    When you really do want your partner to say yes, state that up front and then remind yourself and them that you want to keep talking until you find something that works for both of you.
                  </li>
                </ul>
              </div>

              <div className="bg-purple-900/40 rounded-xl p-5 text-center">
                <p className="text-purple-200 italic">
                  "Your authentic expression is not a threat to our relationship."
                </p>
              </div>

              <div className="bg-amber-900/30 rounded-xl p-5">
                <h4 className="text-amber-300 font-bold mb-3">Reflection</h4>
                <p className="text-slate-200 text-sm">
                  Take a moment now to reflect on how you support meeting needs for both <strong>intimacy</strong> and <strong>autonomy</strong> in your relationship and where you find yourself in demand energy or perceiving demands. Choose at least one of the strategies above to practice with this week.
                </p>
              </div>

              <p className="text-slate-400 text-sm italic text-center">
                — LaShelle Charde's Connection Gem of the Week, www.wiseheartpdx.org
              </p>
            </div>
          </div>
        );

      // PAGE 125 WORKSHEET
      case 10:
        return (
          <div className="space-y-6 animate-fadeIn" data-testid="page125">
            <div className="text-center mb-6">
              <GiftIcon size={40} className="mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-white mb-2">Let's Make Our Life More Wonderful Together</h2>
              <p className="text-amber-300">Page 125 • Requests & Offers Worksheet</p>
            </div>

            <div className="flex justify-center mb-6">
              <FeelingsNeedsCTA />
            </div>

            <div className="bg-amber-900/30 border border-amber-500/30 rounded-xl p-5 mb-6">
              <p className="text-amber-100">
                Use this worksheet to explore strategies for making life more wonderful for yourself and another. 
                In the left column, write any <strong>requests</strong> (top half) or <strong>offers</strong> (bottom half) that you have. 
                In the middle column, write the needs of yours that you think might be met.
              </p>
            </div>

            {/* Requests Section */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-purple-300 mb-4">Specific Requests I Would Like to Ask</h3>
              <p className="text-slate-400 text-sm mb-4">"To make our life more wonderful..."</p>
              
              {[0, 1, 2, 3, 4].map((i) => (
                <div key={`req-${i}`} className="grid grid-cols-3 gap-4 mb-4 items-start">
                  <Textarea
                    value={page125Responses.requests?.[i]?.request || ''}
                    onChange={(e) => {
                      const newRequests = [...(page125Responses.requests || [])];
                      if (!newRequests[i]) newRequests[i] = { request: '', needs: '', name: '' };
                      newRequests[i].request = e.target.value;
                      setPage125Responses(prev => ({ ...prev, requests: newRequests }));
                    }}
                    placeholder="Request..."
                    className="bg-slate-900/50 border-slate-600 text-white min-h-[60px]"
                  />
                  <Textarea
                    value={page125Responses.requests?.[i]?.needs || ''}
                    onChange={(e) => {
                      const newRequests = [...(page125Responses.requests || [])];
                      if (!newRequests[i]) newRequests[i] = { request: '', needs: '', name: '' };
                      newRequests[i].needs = e.target.value;
                      setPage125Responses(prev => ({ ...prev, requests: newRequests }));
                    }}
                    placeholder="Needs I think will be met..."
                    className="bg-slate-900/50 border-slate-600 text-white min-h-[60px]"
                  />
                  <Input
                    value={page125Responses.requests?.[i]?.name || ''}
                    onChange={(e) => {
                      const newRequests = [...(page125Responses.requests || [])];
                      if (!newRequests[i]) newRequests[i] = { request: '', needs: '', name: '' };
                      newRequests[i].name = e.target.value;
                      setPage125Responses(prev => ({ ...prev, requests: newRequests }));
                    }}
                    placeholder="Name"
                    className="bg-slate-900/50 border-slate-600 text-white"
                  />
                </div>
              ))}
            </div>

            {/* Offers Section */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-emerald-300 mb-4">Things I Would Like to Offer</h3>
              <p className="text-slate-400 text-sm mb-4">"To make our life more wonderful..."</p>
              
              {[0, 1, 2, 3, 4].map((i) => (
                <div key={`offer-${i}`} className="grid grid-cols-3 gap-4 mb-4 items-start">
                  <Textarea
                    value={page125Responses.offers?.[i]?.offer || ''}
                    onChange={(e) => {
                      const newOffers = [...(page125Responses.offers || [])];
                      if (!newOffers[i]) newOffers[i] = { offer: '', needs: '', name: '' };
                      newOffers[i].offer = e.target.value;
                      setPage125Responses(prev => ({ ...prev, offers: newOffers }));
                    }}
                    placeholder="What I offer..."
                    className="bg-slate-900/50 border-slate-600 text-white min-h-[60px]"
                  />
                  <Textarea
                    value={page125Responses.offers?.[i]?.needs || ''}
                    onChange={(e) => {
                      const newOffers = [...(page125Responses.offers || [])];
                      if (!newOffers[i]) newOffers[i] = { offer: '', needs: '', name: '' };
                      newOffers[i].needs = e.target.value;
                      setPage125Responses(prev => ({ ...prev, offers: newOffers }));
                    }}
                    placeholder="Needs of mine I think will be met..."
                    className="bg-slate-900/50 border-slate-600 text-white min-h-[60px]"
                  />
                  <Input
                    value={page125Responses.offers?.[i]?.name || ''}
                    onChange={(e) => {
                      const newOffers = [...(page125Responses.offers || [])];
                      if (!newOffers[i]) newOffers[i] = { offer: '', needs: '', name: '' };
                      newOffers[i].name = e.target.value;
                      setPage125Responses(prev => ({ ...prev, offers: newOffers }));
                    }}
                    placeholder="Name"
                    className="bg-slate-900/50 border-slate-600 text-white"
                  />
                </div>
              ))}
            </div>
          </div>
        );

      // 101 IDEAS - Pages 126-129
      case 11:
        return (
          <div className="space-y-6 animate-fadeIn" data-testid="101-ideas">
            <div className="text-center mb-6">
              <IdeasIcon size={40} className="mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-white mb-2">101 Ideas for Offering and Asking</h2>
              <p className="text-amber-300">Pages 126-129 • Launching Pad for Connection</p>
            </div>

            <div className="flex justify-center mb-6">
              <FeelingsNeedsCTA />
            </div>

            <div className="bg-gradient-to-br from-amber-900/30 to-orange-900/20 border border-amber-500/30 rounded-xl p-6 mb-6">
              <p className="text-amber-100 leading-relaxed">
                Perhaps the most basic practice of NVC consciousness is to <strong>offer and ask for that which will make life more wonderful</strong>. When we really believe our needs are a gift then we naturally want to find ways to celebrate life by giving and receiving with everyone in our life.
              </p>
              <p className="text-amber-200/80 mt-3 text-sm italic">
                I encourage you to create a journal of "Ideas for Offering and Asking" to promote your own compassionate consciousness and to share those ideas with others.
              </p>
            </div>

            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {ideasList.map((idea, idx) => (
                  <div 
                    key={idx}
                    className="bg-slate-900/50 border border-slate-700/30 rounded-lg p-3 hover:border-amber-500/50 hover:bg-amber-900/10 transition-colors"
                  >
                    <span className="text-amber-400 font-bold mr-2">{idx + 1}.</span>
                    <span className="text-slate-200 text-sm">{idea}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      // 101 MAGIC WORDS - Pages 130-131
      case 12:
        return (
          <div className="space-y-6 animate-fadeIn" data-testid="101-magic">
            <div className="text-center mb-6">
              <SparkleIcon size={40} className="mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-white mb-2">101 Magic Words and Phrases</h2>
              <p className="text-pink-300">Pages 130-131 • Words I Love to Hear</p>
            </div>

            <div className="flex justify-center mb-6">
              <FeelingsNeedsCTA />
            </div>

            <div className="bg-gradient-to-br from-pink-900/30 to-rose-900/20 border border-pink-500/30 rounded-xl p-6 mb-6">
              <p className="text-pink-100 leading-relaxed">
                Most people have certain words and phrases that they like to hear from the people in their life. Things that when said sincerely can powerfully meet needs for things like friendship, love, support, and encouragement.
              </p>
              <p className="text-pink-200/80 mt-3 text-sm">
                <strong>Three important things to remember:</strong>
              </p>
              <ol className="text-pink-100/90 text-sm mt-2 space-y-1">
                <li>1. Only say a magic phrase if it is authentic for you.</li>
                <li>2. Avoid compliments (positive judgments). Instead share feelings and needs.</li>
                <li>3. Use whatever words sound most natural to you and the person you're talking to.</li>
              </ol>
            </div>

            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
              <div className="grid md:grid-cols-2 gap-3">
                {magicPhrases.map((phrase, idx) => (
                  <div 
                    key={idx}
                    className="bg-gradient-to-r from-pink-900/20 to-rose-900/20 border border-pink-500/20 rounded-lg p-3 hover:border-pink-400/50 transition-colors"
                  >
                    <span className="text-pink-400 font-bold mr-2">{idx + 1}.</span>
                    <span className="text-slate-200 text-sm italic">"{phrase}"</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-purple-900/30 border border-purple-500/30 rounded-xl p-5 text-center">
              <p className="text-purple-200 italic">
                Explore with the people in your life what the magic words and phrases are that create more connection and closeness.
              </p>
            </div>
          </div>
        );

      // HOMEWORK - Page 134
      case 13:
        return (
          <div className="space-y-6 animate-fadeIn" data-testid="homework">
            <div className="text-center mb-6">
              <HomeworkIcon size={40} className="mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-white mb-2">Chapter 5 Homework</h2>
              <p className="text-purple-300">Page 134</p>
            </div>

            <div className="flex justify-center mb-6">
              <FeelingsNeedsCTA />
            </div>

            {/* Reading */}
            <div className="bg-gradient-to-br from-blue-900/30 to-indigo-900/30 border border-blue-500/30 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <ReadingIcon size={28} />
                <h3 className="text-xl font-bold text-blue-300">Reading</h3>
              </div>
              <ul className="space-y-3 text-slate-200">
                <li className="flex items-start gap-3">
                  <span className="text-blue-400">•</span>
                  <span>Read pages 118-132 in this notebook: "What Keeps Me From Making a Request," "Don't Criticize Me vs. Do-able Requests," "When Every Request is a Demand," "Let's Make Our Life Together More Wonderful"</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-blue-400">•</span>
                  <span>Read chapters 7, 8, and 9 in <em>The NVC Book</em>: "Receiving Empathically," "The Power of Empathy," and "Connecting Compassionately With Ourselves"</span>
                </li>
              </ul>
            </div>

            {/* Video */}
            <div className="bg-gradient-to-br from-purple-900/30 to-violet-900/30 border border-purple-500/30 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <VideoIcon size={28} />
                <h3 className="text-xl font-bold text-purple-300">Watch Video</h3>
              </div>
              <p className="text-slate-200">
                Part 4 of the NVC basics video: <strong>"Questions and Expressing & Receiving Gratitude"</strong>
                <br />
                <span className="text-purple-400">Time marker 2:23:25-3:05:57</span> on the YouTube version.
              </p>
            </div>

            {/* Partner Exercises */}
            <div className="bg-gradient-to-br from-emerald-900/30 to-teal-900/30 border border-emerald-500/30 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <PartnerIcon size={28} />
                <h3 className="text-xl font-bold text-emerald-300">Partner Exercises</h3>
              </div>
              
              <div className="space-y-6">
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <h4 className="text-emerald-200 font-semibold mb-2">Exercise 1: Connecting Requests Role-Play</h4>
                  <p className="text-slate-300 text-sm mb-3">
                    You will be practicing making connecting requests in a role-play. The speaker will think of 3 situations where s/he expressed an Observation, Feeling and Need. The speaker will say this out loud to the listener and then add a <strong>CONNECTING request</strong>. The listener will respond in the role-play either by taking it as a criticism, a demand, or an attack. The speaker will re-assert the need.
                  </p>
                  <div className="bg-slate-900/50 rounded p-3 text-sm">
                    <p className="text-slate-400 mb-2"><strong>Example:</strong></p>
                    <p className="text-emerald-300"><strong>Speaker:</strong> "When I saw you return from the store without the food I asked you to get, I felt disappointed and frustrated because I'm wanting cooperation and support. Can you tell me what you heard me say?"</p>
                    <p className="text-rose-300 mt-2"><strong>Listener:</strong> "Yeah, you said I was incompetent."</p>
                    <p className="text-emerald-300 mt-2"><strong>Speaker:</strong> "Thanks for telling me what you heard. What I was really hoping you would connect with is the disappointment I felt because I was wanting cooperation and ease. Can you tell me what you're hearing me say?"</p>
                  </div>
                </div>

                <div className="bg-slate-800/50 rounded-lg p-4">
                  <h4 className="text-emerald-200 font-semibold mb-2">Exercise 2: Strategy Requests from Chapter 4</h4>
                  <p className="text-slate-300 text-sm">
                    Before you get together with your partner, make sure you have completed <strong>Exercise 2 in the Needs chapter</strong> of this Workbook. Use your own high priority needs and think of a scenario where that particular need was not being met. Express a scenario in the form of an Observation, Feeling, and Need and then add a <strong>Strategy Request</strong>.
                  </p>
                  <p className="text-slate-300 text-sm mt-3">
                    The listener's role is to determine whether the strategy request meets the following criteria: positive action language, who, what, where, when, how, for how long. Each person does this for <strong>15 minutes</strong> and should use as many needs as required to fill the allotted time.
                  </p>
                </div>
              </div>
            </div>

            {/* Completion */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6 text-center">
              <p className="text-slate-300 mb-4">
                You've completed <span className="text-purple-400 font-bold">Chapter 5: Requests</span>! 
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Button
                  onClick={() => navigate('/workbook/compassionate-wheel-enhanced')}
                  variant="outline"
                  className="border-emerald-600 text-emerald-300 hover:bg-emerald-900/30"
                >
                  ← Communication Wheel
                </Button>
                <Button
                  onClick={() => navigate('/belonging-ebook')}
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white"
                >
                  <ChapterIcon size={18} className="mr-2" />
                  Return to eBook
                </Button>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 ${isPresentationMode ? 'overflow-hidden' : ''}`}>
      {/* Header */}
      <header className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur-md border-b border-slate-700/50">
        <div className="max-w-5xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/belonging-ebook')}
                variant="ghost"
                size="sm"
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeftIcon size={16} className="mr-1" />
                Back
              </Button>
              <div className="h-5 w-px bg-slate-700" />
              <div>
                <h1 className="text-lg font-bold text-white">Chapter 5: Requests</h1>
                <p className="text-xs text-purple-400">Pages 114-134</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {isLoggedIn ? (
                <div className="flex items-center gap-1 text-xs text-slate-500">
                  {isSyncing ? (
                    <CloudIcon size={16} color="#60A5FA" className="animate-pulse" />
                  ) : (
                    <CloudIcon size={16} color="#34D399" />
                  )}
                </div>
              ) : (
                <CloudOffIcon size={16} />
              )}
              <Button onClick={saveProgress} variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                <SaveIcon size={16} className="mr-1" />
                Save
              </Button>
              <Button onClick={downloadPDF} variant="ghost" size="sm" className="text-slate-400 hover:text-white" title="Download as PDF">
                <DownloadIcon size={16} className="mr-1" />
                PDF
              </Button>
              <Button onClick={resetAll} variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                <ResetIcon size={16} />
              </Button>
              <Button onClick={togglePresentationMode} variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                {isPresentationMode ? <CollapseIcon size={16} /> : <ExpandIcon size={16} />}
              </Button>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-3">
            <Progress value={progress_pct} className="h-1.5 bg-slate-700" />
            <div className="flex justify-between mt-1 text-xs text-slate-500">
              <span>Section {currentSection + 1} of {sections.length}</span>
              <span>Page {sections[currentSection]?.page}</span>
            </div>
          </div>
        </div>
      </header>

      {/* Section Navigation */}
      {!isPresentationMode && (
        <div className="bg-slate-800/50 border-b border-slate-700/30">
          <div className="max-w-5xl mx-auto px-4 py-2">
            <div className="flex gap-2 overflow-x-auto pb-2">
              {sections.map((section, idx) => (
                <button
                  key={section.id}
                  onClick={() => setCurrentSection(idx)}
                  className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition-all ${
                    currentSection === idx
                      ? 'bg-purple-600 text-white'
                      : 'bg-slate-700/50 text-slate-400 hover:bg-slate-600/50 hover:text-slate-200'
                  }`}
                >
                  {section.title}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className={`max-w-4xl mx-auto px-4 py-8 ${isPresentationMode ? 'h-screen flex items-center' : ''}`}>
        <div className="w-full">
          {renderContent()}
        </div>
      </main>

      {/* Navigation Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-slate-900/95 backdrop-blur-md border-t border-slate-700/50 py-3 px-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Button
            onClick={() => setCurrentSection(prev => Math.max(prev - 1, 0))}
            disabled={currentSection === 0}
            variant="outline"
            className="border-slate-600 text-slate-300 hover:bg-slate-800 disabled:opacity-30"
          >
            <ArrowLeftIcon size={18} className="mr-2" />
            Previous
          </Button>
          
          <div className="text-center">
            <span className="text-slate-400 text-sm">{sections[currentSection]?.title}</span>
          </div>
          
          <Button
            onClick={() => setCurrentSection(prev => Math.min(prev + 1, sections.length - 1))}
            disabled={currentSection === sections.length - 1}
            className="bg-purple-600 hover:bg-purple-500 text-white disabled:opacity-30"
          >
            Next
            <ArrowRightIcon size={18} className="ml-2" />
          </Button>
        </div>
      </div>

      {/* Floating Feelings & Needs Helper */}
      <FloatingFeelingsHelper />

      {/* Feelings & Needs Helper Modal */}
      <FeelingsNeedsHelper 
        isOpen={isHelperOpen} 
        onClose={() => setIsHelperOpen(false)} 
      />
    </div>
  );
}
